/**
 * BlogPostPage.jsx
 * 
 * Individual blog post page accessed via /blog/:slug URL.
 * Includes SEO head with meta tags and JSON-LD.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, BookOpen, ChevronRight, ExternalLink, AlertCircle, Link2, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPostBySlug, getRelatedPosts } from '../data/blogData';
import { toolData } from '../data/toolData';
import { getApprovedAuthor } from '../data/authors.js';
import { HOMEPAGE_ROUTE, EXPLORER_ROUTE } from '../data/siteConfig.js';
import SEOHead, { extractFAQFromContent } from './SEOHead';
import GlassSurface from './GlassSurface';
import { Callout, parseCalloutFromBlockquote, ComparisonTable, FAQAccordion, AtAGlanceTable, EvidencePanel, FreshnessTimeline, ShortAnswerBlock } from './BlogComponents';

const CANONICAL = 'https://touchstone.guide';
import ShinyText from './ShinyText';
import Footer from './Footer';

// Reading progress bar — tracks scroll through the article
function ReadingProgress({ containerRef }) {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const scrollable = el.scrollHeight - el.clientHeight;
      if (scrollable <= 0) return;
      const pct = el.scrollTop / scrollable;
      setProgress(Math.min(1, Math.max(0, pct)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [containerRef]);
  
  return (
    <div className="fixed right-0 top-0 bottom-0 z-[70] w-[3px] bg-white/[0.04] pointer-events-none">
      <div
        className="w-full bg-gradient-to-b from-teal-400 to-emerald-400 transition-[height] duration-150 ease-out rounded-b-full"
        style={{ height: `${progress * 100}%` }}
      />
    </div>
  );
}

// Navigation pages
const pages = [
  { id: 'explore', label: 'explore', path: EXPLORER_ROUTE },
  { id: 'workflow', label: 'workflow', path: '/workflow' },
  { id: 'database', label: 'database', path: '/database' },
  { id: 'blog', label: 'blog', path: '/blog' },
];

// Navigation header component — matches AIToolExplorer header exactly
function NavigationHeader() {
  const location = useLocation();
  const currentPage = location.pathname.startsWith('/blog') ? 'blog' : 'explore';
  
  return (
    <div className="fixed top-4 left-4 right-4 z-[60]">
      <GlassSurface
        height={64}
        borderRadius={16}
        brightness={40}
        opacity={0.9}
        className="w-full"
        style={{ width: '100%' }}
      >
        <div className="flex items-center justify-between w-full h-full px-6">
          {/* Logo with Page Indicator */}
          <div className="flex items-center">
            <Link to={HOMEPAGE_ROUTE} className="hover:opacity-80 transition-opacity">
              <div
                style={{ 
                  fontFamily: "'Qulio Display', 'Bodoni Moda', serif",
                  display: 'inline-block',
                  letterSpacing: '0.05em',
                }}
              >
                <span className="text-4xl text-white">TOUCHSTONE</span>
              </div>
            </Link>
            
            {/* Navigation Items */}
            <div className="flex items-center ml-3">
              {pages.map((page, index) => (
                <Link
                  key={page.id}
                  to={page.path}
                  className={`flex items-center hover:opacity-80 transition-opacity ${index > 0 ? 'ml-4' : ''}`}
                >
                  <div 
                    className={`w-2.5 h-2.5 border-r-2 border-b-2 rotate-[-45deg] ${
                      currentPage === page.id ? 'border-white' : 'border-white/40'
                    }`}
                    style={{ marginRight: '8px' }}
                  />
                  {currentPage === page.id ? (
                    <span 
                      className="text-lg font-medium text-white"
                      style={{
                        fontFamily: "'Qulio Display', 'Bodoni Moda', serif",
                        letterSpacing: '0.05em',
                        textShadow: '0 0 6px rgba(192, 192, 192, 0.5), 0 0 12px rgba(192, 192, 192, 0.35), 0 0 18px rgba(192, 192, 192, 0.2)',
                        filter: 'drop-shadow(0 0 4px rgba(192, 192, 192, 0.5)) drop-shadow(0 0 8px rgba(192, 192, 192, 0.3))'
                      }}
                    >
                      {page.label}
                    </span>
                  ) : (
                    <span 
                      className="text-lg font-medium text-white/40"
                      style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
                    >
                      {page.label}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Right Side Controls — matches AIToolExplorer */}
          <div className="flex items-center gap-2">
            <Link 
              to={EXPLORER_ROUTE}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all"
            >
              <ShinyText 
                text="Profile" 
                speed={3.7}
                className="text-sm font-medium"
                color="#ffffff"
                shineColor="#14b8a6"
              />
            </Link>
          </div>
        </div>
      </GlassSurface>
    </div>
  );
}

// Category colors matching the app
const categoryColors = {
  'Language Model': '#10b981',
  'Vector Database': '#64748b',
  'Orchestration': '#14b8a6',
  'AI Tools': '#14b8a6',
  'Code Assistant': '#22c55e',
  'Agents': '#8b5cf6',
  'Data Processing': '#a78bfa',
  'Image Generation': '#f43f5e',
  'Audio/Voice': '#ec4899',
};

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// Tool chip component
function ToolChip({ toolName }) {
  const tool = toolData.find(t => t.name === toolName);
  if (!tool) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/[0.06] text-zinc-300 border border-white/[0.08]">
        {toolName}
      </span>
    );
  }
  
  return (
    <span 
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${tool.bg}20`, color: tool.bg }}
    >
      {toolName}
    </span>
  );
}

// 404 component for missing posts
function PostNotFound({ slug }) {
  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text blog-scrollbar">
      {/* Navigation Header */}
      <NavigationHeader />
      
      <div className="min-h-screen pt-36 pb-6 px-6">
        <SEOHead
          title="Post Not Found"
          description="The requested blog post could not be found."
          path={`/blog/${slug}`}
          noindex={true}
        />
        <div className="max-w-4xl mx-auto text-center py-16">
          <AlertCircle className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-white mb-2">Post Not Found</h1>
          <p className="text-zinc-400 mb-6">
            The article "{slug}" doesn't exist or may have been moved.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  
  const post = getPostBySlug(slug);
  
  // Handle 404
  if (!post) {
    return <PostNotFound slug={slug} />;
  }
  
  const relatedPosts = getRelatedPosts(post.slug);
  const categoryColor = categoryColors[post.category] || '#71717a';
  const primaryToolId = post.toolIds?.find(id => toolData.some(tool => tool.id === id));
  const author = getApprovedAuthor(post.author);
  const relatedTools = useMemo(
    () => (post.tools || []).map((toolName) => toolData.find((tool) => tool.name === toolName)).filter(Boolean),
    [post.tools]
  );
  
  // Extract FAQ from content for structured data
  const faq = extractFAQFromContent(post.content);
  const pricingSources = useMemo(
    () => relatedTools.map((tool) => tool.pricing_source?.url).filter(Boolean),
    [relatedTools]
  );
  const docsChecked = useMemo(
    () => relatedTools.flatMap((tool) => {
      const docs = [];
      if (tool.technical_specs?.source_url) docs.push(`${tool.name} technical documentation`);
      if (tool.documentation_quality?.api_docs) docs.push(`${tool.name} API docs`);
      if (tool.documentation_quality?.tutorials) docs.push(`${tool.name} tutorials`);
      if (tool.documentation_quality?.changelog) docs.push(`${tool.name} changelog`);
      return docs;
    }),
    [relatedTools]
  );
  const knownUnknowns = useMemo(
    () => [
      ...relatedTools.flatMap((tool) =>
        (tool.limitations || []).slice(0, 1).map((item) => `${tool.name}: ${item.limitation}`)
      ),
      'Pricing, limits, and vendor positioning can change between review cycles.',
    ].slice(0, 5),
    [relatedTools]
  );
  const freshnessItems = useMemo(
    () => [
      post.dateModified && {
        label: post.dateModified,
        detail: 'Article copy and recommendations were updated.',
      },
      post.lastVerified && {
        label: post.lastVerified,
        detail: 'Facts and references were re-checked for this article.',
      },
      post.datePublished && {
        label: post.datePublished,
        detail: 'Article first published on Touchstone.',
      },
    ].filter(Boolean),
    [post]
  );
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Handle navigation to explore page with tool selected
  const handleExplore = (toolId) => {
    navigate('/', { state: { selectToolId: toolId } });
  };
  
  return (
    <div ref={scrollContainerRef} className="fixed inset-0 overflow-y-auto bg-[#030303] select-text blog-scrollbar">
      {/* Reading Progress Bar */}
      <ReadingProgress containerRef={scrollContainerRef} />
      
      {/* Navigation Header */}
      <NavigationHeader />
      
      <main className="min-h-screen pt-36 pb-6 px-6">
      {/* SEO Head with article metadata */}
      <SEOHead
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.heroImage}
        article={{
          publishedAt: post.datePublished || post.lastVerified,
          updatedAt: post.dateModified || post.lastVerified,
          authorKey: post.author,
        }}
        faq={faq}
        breadcrumbs={[
          { name: 'Home', url: `${CANONICAL}/home` },
          { name: 'Blog', url: `${CANONICAL}/blog` },
          { name: post.title, url: `${CANONICAL}/blog/${post.slug}` },
        ]}
      />
      
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <nav aria-label="Back navigation">
          <Link
            to="/blog"
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to all posts</span>
          </Link>
        </nav>
        
        <article>
        {/* Header */}
        <header className="mb-8">
          {/* Category & Meta */}
          <div className="flex items-center gap-4 mb-4">
            <span 
              className="text-xs font-medium px-3 py-1.5 rounded-full"
              style={{ backgroundColor: `${categoryColor}20`, color: categoryColor }}
            >
              {post.category}
            </span>
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Last reviewed: {post.lastVerified}
            </span>
            {author && (
              <span className="text-xs text-zinc-500">
                By {author.name}, {author.title}
              </span>
            )}
            <button
              onClick={handleCopyLink}
              className="ml-auto flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-300 transition-all duration-200"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-teal-400" />
                  <span className="text-teal-400">Copied!</span>
                </>
              ) : (
                <>
                  <Link2 className="w-3 h-3" />
                  Copy link
                </>
              )}
            </button>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-semibold text-white mb-4">
            {post.title}
          </h1>
          
          {/* TL;DR / Direct Answer */}
          {post.directAnswer && (
            <div className="bg-teal-500/5 border border-teal-500/20 rounded-xl p-5 mb-6" id="direct-answer" data-section="direct-answer">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-400">TL;DR</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                {post.directAnswer}
              </p>
            </div>
          )}
          
          {/* Description fallback if no directAnswer */}
          {!post.directAnswer && (
            <p className="text-lg text-zinc-400 mb-6">
              {post.description}
            </p>
          )}

          {post.heroImage && (
            <div className="mb-6 overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03]">
              <img
                src={post.heroImage}
                alt={post.heroImageAlt || post.title}
                className="block w-full h-auto"
                loading="eager"
              />
            </div>
          )}
          
          {/* Tools covered */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500">Tools covered:</span>
            <div className="flex flex-wrap gap-2">
              {post.tools.map(tool => (
                <ToolChip key={tool} toolName={tool} />
              ))}
            </div>
          </div>
        </header>
        
        {/* At A Glance — structured metadata table */}
        {post.atAGlance && <AtAGlanceTable rows={post.atAGlance} />}

        <EvidencePanel
          lastVerified={post.lastVerified || post.dateModified}
          sourceTypes={[
            'Touchstone editorial review',
            relatedTools.length > 0 && 'Tool database records',
            pricingSources.length > 0 && 'Vendor pricing pages',
            faq.length > 0 && 'Structured FAQ answers',
          ].filter(Boolean)}
          pricingSource={pricingSources.length > 0 ? pricingSources.join(' | ') : 'See vendor pricing references linked from the covered tool pages.'}
          docsChecked={docsChecked}
          knownUnknowns={knownUnknowns}
        />

        <FreshnessTimeline items={freshnessItems} />

        {relatedTools.length > 0 && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            <div className="bg-white/[0.02] border border-white/[0.08] rounded-xl p-5">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Tool profiles mentioned here</div>
              <div className="space-y-3">
                {relatedTools.slice(0, 4).map((tool) => (
                  <Link
                    key={tool.id}
                    to={`/database/${slugify(tool.name)}`}
                    className="block rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/[0.14] hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="text-sm text-white font-medium mb-1">{tool.name}</div>
                    <div className="text-xs text-zinc-500">{tool.category || 'AI tool'} profile in the Touchstone database</div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.08] rounded-xl p-5">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Continue this evaluation</div>
              <div className="space-y-3">
                <Link
                  to="/database"
                  className="block rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/[0.14] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="text-sm text-white font-medium mb-1">Browse more tool profiles</div>
                  <div className="text-xs text-zinc-500">Jump from this comparison into adjacent categories and alternatives.</div>
                </Link>
                <Link
                  to="/workflow"
                  className="block rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/[0.14] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="text-sm text-white font-medium mb-1">Turn this research into a workflow</div>
                  <div className="text-xs text-zinc-500">Use Touchstone workflow planning to map shortlisted tools into a stack.</div>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Comparison Table — structured feature comparison */}
        {post.comparisonData && (
          <ComparisonTable
            tools={post.comparisonData.tools}
            features={post.comparisonData.features}
            verifiedDate={post.comparisonData.verifiedDate || post.lastVerified}
          />
        )}

        {/* Main Content */}
        <div className="prose prose-invert prose-zinc max-w-none mb-12 select-text">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Glass-style table with zebra striping and sticky header
              table: ({ node, ...props }) => (
                <div className="my-6 bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse" {...props} />
                  </div>
                </div>
              ),
              thead: ({ node, ...props }) => (
                <thead className="bg-white/[0.04] sticky top-0 z-10" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider border-b border-white/[0.08]" {...props} />
              ),
              tr: ({ node, ...props }) => (
                <tr className="even:bg-white/[0.02] transition-colors" {...props} />
              ),
              td: ({ node, ...props }) => (
                <td className="px-5 py-3.5 text-sm text-zinc-300 border-b border-white/[0.05]" {...props} />
              ),
              // Custom heading styling
              h1: ({ node, ...props }) => (
                <h2 className="text-xl font-medium text-white mt-8 mb-4 pb-2 border-b border-zinc-800" {...props} />
              ),
              h2: ({ node, ...props }) => (
                <h2 className="text-xl font-medium text-white mt-8 mb-4 pb-2 border-b border-zinc-800" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-lg font-medium text-zinc-200 mt-6 mb-3" {...props} />
              ),
              // Custom list styling
              ul: ({ node, ...props }) => (
                <ul className="list-disc list-inside space-y-2 text-zinc-300" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal list-inside space-y-2 text-zinc-300" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="text-zinc-300" {...props} />
              ),
              // Paragraphs
              p: ({ node, children, ...props }) => {
                const childArray = React.Children.toArray(children);
                const firstChild = childArray[0];
                const startsWithStrong = React.isValidElement(firstChild) && firstChild.type === 'strong';

                if (startsWithStrong) {
                  return (
                    <ShortAnswerBlock>
                      <p className="text-zinc-300 leading-relaxed m-0" {...props}>{children}</p>
                    </ShortAnswerBlock>
                  );
                }

                return <p className="text-zinc-300 leading-relaxed mb-4" {...props}>{children}</p>;
              },
              // Links
              a: ({ node, href, ...props }) => (
                href?.startsWith('/') ? (
                  <Link
                    to={href}
                    className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                    {...props}
                  />
                ) : href?.startsWith('#') ? (
                  <a
                    href={href}
                    className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                    {...props}
                  />
                ) : (
                  <a 
                    href={href} 
                    className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props} 
                  />
                )
              ),
              // Strong
              strong: ({ node, ...props }) => (
                <strong className="text-white font-medium" {...props} />
              ),
              // Code blocks
              code: ({ node, inline, className, children, ...props }) => {
                if (inline) {
                  return (
                    <code className="px-1.5 py-0.5 bg-zinc-800 rounded text-teal-300 text-sm" {...props}>
                      {children}
                    </code>
                  );
                }
                return (
                  <pre className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 overflow-x-auto">
                    <code className="text-sm text-zinc-300" {...props}>
                      {children}
                    </code>
                  </pre>
                );
              },
              // Blockquotes — detect [!type] callout syntax
              blockquote: ({ node, children, ...props }) => {
                const callout = parseCalloutFromBlockquote(children);
                if (callout) {
                  return <Callout type={callout.type}>{callout.content}</Callout>;
                }
                return (
                  <blockquote className="border-l-4 border-teal-500 pl-4 py-2 my-4 text-zinc-400 italic" {...props}>
                    {children}
                  </blockquote>
                );
              },
              // Horizontal rules
              hr: ({ node, ...props }) => (
                <hr className="border-zinc-800 my-8" {...props} />
              ),
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* FAQ Accordion — interactive Q&A from extracted content */}
        {faq.length > 0 && <FAQAccordion items={faq} />}
        
        {/* Explore on Touchstone CTA */}
        <div className="bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-medium text-white mb-1">
                {primaryToolId ? 'See these tools in context' : 'Explore related tool coverage'}
              </h3>
              <p className="text-sm text-zinc-400">
                {primaryToolId
                  ? `Explore ${post.tools.join(' and ')} on the Touchstone globe with fit scores and capability profiles.`
                  : `These tools are covered in Touchstone research, but they are not yet mapped to the interactive globe. Browse the database and related articles for adjacent coverage.`}
              </p>
            </div>
            {primaryToolId ? (
              <button
                onClick={() => handleExplore(primaryToolId)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors"
              >
                <span>Explore</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            ) : (
              <Link
                to="/database"
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors"
              >
                <span>Browse database</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
        
        {/* Related Posts — glass mini-cards */}
        {relatedPosts.length > 0 && (
          <section className="border-t border-white/[0.06] pt-12 mt-4">
            <h2 className="text-xl font-semibold text-white mb-6">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {relatedPosts.map(related => {
                const relColor = categoryColors[related.category] || '#71717a';
                return (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="group relative block rounded-xl overflow-hidden transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(20,184,166,0.06)]"
                  >
                    <div className="absolute inset-0 bg-white/[0.03] border border-white/[0.08] rounded-xl group-hover:border-white/[0.15] transition-all duration-300" />
                    {/* Category accent strip */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                      style={{ backgroundColor: `${relColor}50` }}
                    />
                    <div className="relative p-6 pl-7">
                      <div className="flex items-center gap-2 mb-3">
                        <span 
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider"
                          style={{ backgroundColor: `${relColor}15`, color: relColor, border: `1px solid ${relColor}20` }}
                        >
                          {related.category}
                        </span>
                        <span className="text-[11px] text-zinc-500 flex items-center gap-1 ml-auto">
                          <Clock className="w-3 h-3" />
                          {related.readTime}
                        </span>
                      </div>
                      <div className="text-base font-medium text-white group-hover:text-teal-400 transition-colors duration-200 leading-snug mb-2">
                        {related.title}
                      </div>
                      {related.description && (
                        <p className="text-sm text-zinc-500 line-clamp-2">{related.description}</p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
        </article>
      </div>
      </main>
      <Footer />
    </div>
  );
}
